import { useIntl } from "react-intl";

import { TwcPromo } from "@/components/pricing-promos/src/twc-promo.tsx";
import { PricingFeatureTable } from "@/components/wpl/pricing/src/features-table";
import { QuestionsCta } from "@/components/wpl/pricing/src/index.ts";
import { usePricingContentContext } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { QuotationsAdornment } from "@/components/wpl/pricing/src/pricing-faq";
import { PricingFaq } from "@/components/wpl/pricing/src/pricing-faq/pricing-faq";
import {
    PricingPageLayout,
    PageHeading
} from "@/components/wpl/pricing/src/pricing-page-layout";
import { Link } from "@/libraries/design/components/index.ts";
import { useLocalizedHref } from "@/libraries/web-framework/index.ts";
import LoomNav from "@/templates/software/loom/pricing/sections/loom-navbar";
import type { CallToAction } from "@/utils/pricing-queries/get-pricing-content-gql.ts";
import { ParentScrollTracking } from "@/utils/scroll-tracking/parent-scroll-tracker.tsx";
import { UserLocale } from "@/utils/useGeoCurrency.ts";

import { AppPricingData } from "../../../../contentful/index.tsx";

import { LoomAiSection } from "./sections/loom-ai-section.tsx";
import { LoomPriceFinder } from "./sections/loom-price-finder.tsx";
import { usePremiumSignupUrl } from "./sections/urls.ts";

export interface LoomPricingPageProps {
    pricing?: AppPricingData;
    countryCode?: UserLocale;
}

// eslint-disable-next-line complexity
export const LoomPricingPage = (props: LoomPricingPageProps) => {
    const { pricing, countryCode } = props;

    const content = usePricingContentContext();

    const featuresCtaContent = content
        ?.callToActionContainer?.[1] as CallToAction;
    const questionsCtaContent = content
        ?.callToActionContainer?.[0] as CallToAction;

    const intl = useIntl();

    const defaultQuestionsCtaHrefPath = usePremiumSignupUrl();

    return (
        <PricingPageLayout nav={<LoomNav />} showFallback={content?.hasError}>
            <ParentScrollTracking
                scrollTrackingEnabled={true}
                pageId="loom-pricing-page"
                as="main"
            >
                <PageHeading>{content?.headline}</PageHeading>
                <LoomPriceFinder pricing={pricing} countryCode={countryCode} />
                <LoomAiSection />
                <PricingFeatureTable
                    title={content?.compareFeaturesTitle ?? undefined}
                    comparePlans={content?.comparePlans ?? undefined}
                    compareFeatures={content?.compareFeatures ?? undefined}
                    linkValues={{
                        linkText: featuresCtaContent?.text ?? undefined,
                        linkHref: featuresCtaContent?.url ?? undefined
                    }}
                    dataProductKey={"loom"}
                    stickyCtasEnabled={false}
                />
                <Link
                    href={useLocalizedHref(
                        "/teams/nonprofits/discount-pricing?tab=loom"
                    )}
                    text={intl.formatMessage({
                        id: "LoomPricingPage.educationDiscount",
                        defaultMessage:
                            "Are you a teacher or a student? Take advantage of our Education discount"
                    })}
                />

                <TwcPromo product="loom" />

                <QuotationsAdornment>
                    <ParentScrollTracking>
                        <PricingFaq
                            header={content?.getMoreDetailsTitle ?? undefined}
                            planDetails={
                                content?.additionalDetails ?? undefined
                            }
                        />
                    </ParentScrollTracking>
                </QuotationsAdornment>
                <QuestionsCta
                    product={"Loom"}
                    ctaHref={
                        questionsCtaContent?.url ?? defaultQuestionsCtaHrefPath
                    }
                    questionsHeader={questionsCtaContent?.title ?? undefined}
                    priceAndLicensingLinkText={
                        questionsCtaContent?.text ?? undefined
                    }
                    questionsBody={
                        questionsCtaContent?.valueProposition ?? undefined
                    }
                    imageHref={questionsCtaContent?.asset?.url}
                />
            </ParentScrollTracking>
        </PricingPageLayout>
    );
};
